/**
 * Admin 프로젝트 변수 문서 편집기 — 라우트 admin.project-docs.* 와 동기화.
 *
 * edit():  프런트매터 필드(title/desc/categories/related/updatedAt) + 본문 textarea 입력을
 *          감지해 아래쪽 풀-와이드 미리보기(메타박스 + 마크다운 렌더)를 라이브로 갱신한다.
 *          저장은 폼의 data-admin-ajax="1" 에 의해 AdminForm.ts(공통)가 처리하므로 별도 동작 없음.
 *          (서버는 필드별 값을 받아 YAML 프런트매터로 재조립한다.)
 *
 * 뷰: resources/views/admin/pages/project-docs/edit.php
 * 컨트롤러(PHP): app/Controllers/Admin/AdminProjectDocController.php
 */
import { Logger } from "../../utils/Logger";

const CTRL_TAG = "AdminProjectDocController";

/* --------------------------------------------------------------------------
 * 가벼운 마크다운 → HTML 렌더러 (프리뷰 전용)
 * 지원: ATX 헤딩 / 코드펜스 / 인라인코드 / 굵게·기울임 / 링크 / 목록(ul·ol) /
 *       인용 / 수평선 / 단순 표 / 문단. HTML 입력은 이스케이프한다.
 * ------------------------------------------------------------------------ */

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function renderInline(text: string): string {
  let out = escapeHtml(text);

  // 인라인 코드 (`code`) — 다른 변환보다 먼저, 내부는 그대로
  const codeSpans: string[] = [];
  out = out.replace(/`([^`]+)`/g, (_m, c: string) => {
    codeSpans.push(c);
    return ` CODE${codeSpans.length - 1} `;
  });

  // 굵게
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  // 기울임 (단어 경계 단순 처리)
  out = out.replace(/(^|[^*])\*([^*\s][^*]*?)\*(?!\*)/g, "$1<em>$2</em>");
  out = out.replace(/(^|[^_\w])_([^_\s][^_]*?)_(?![_\w])/g, "$1<em>$2</em>");
  // 링크 [text](url)
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, label: string, url: string) => {
    const safe = /^(https?:\/\/|mailto:|#|\/|\.\.?\/)/i.test(url) ? url : "#";
    const external = /^https?:\/\//i.test(safe);
    const attrs = external ? ' target="_blank" rel="noopener noreferrer"' : "";
    return `<a href="${safe}"${attrs}>${label}</a>`;
  });

  // 코드 스팬 복원
  out = out.replace(/ CODE(\d+) /g, (_m, i: string) => `<code>${codeSpans[Number(i)]}</code>`);

  return out;
}

function isTableSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(line);
}

function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.trim());
}

function renderMarkdown(src: string): string {
  const lines = src.replace(/\r\n?/g, "\n").split("\n");
  const html: string[] = [];

  let inCode = false;
  let codeBuf: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let paraBuf: string[] = [];

  const flushPara = (): void => {
    if (paraBuf.length) {
      html.push(`<p>${renderInline(paraBuf.join(" "))}</p>`);
      paraBuf = [];
    }
  };
  const flushList = (): void => {
    if (listType) {
      html.push(`</${listType}>`);
      listType = null;
    }
  };
  const flushBlocks = (): void => {
    flushPara();
    flushList();
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // 코드펜스 ``` 또는 ~~~
    if (/^\s*(```|~~~)/.test(line)) {
      if (inCode) {
        html.push(`<pre><code>${escapeHtml(codeBuf.join("\n"))}</code></pre>`);
        codeBuf = [];
        inCode = false;
      } else {
        flushBlocks();
        inCode = true;
      }
      continue;
    }
    if (inCode) {
      codeBuf.push(line);
      continue;
    }

    // 빈 줄 → 블록 종료
    if (/^\s*$/.test(line)) {
      flushBlocks();
      continue;
    }

    // 수평선
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      flushBlocks();
      html.push("<hr>");
      continue;
    }

    // ATX 헤딩
    const heading = line.match(/^(#{1,6})\s+(.*?)\s*#*\s*$/);
    if (heading) {
      flushBlocks();
      const level = heading[1].length;
      html.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
      continue;
    }

    // 인용
    const quote = line.match(/^\s*>\s?(.*)$/);
    if (quote) {
      flushPara();
      flushList();
      html.push(`<blockquote>${renderInline(quote[1])}</blockquote>`);
      continue;
    }

    // 표: 헤더 줄 + 다음 줄이 구분선이면 표로 처리
    if (line.includes("|") && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      flushBlocks();
      const headers = splitRow(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].includes("|") && !/^\s*$/.test(lines[i])) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      i--; // for 루프에서 다시 ++ 되므로 보정
      const thead = `<thead><tr>${headers.map((h) => `<th>${renderInline(h)}</th>`).join("")}</tr></thead>`;
      const tbody = `<tbody>${rows
        .map((r) => `<tr>${r.map((c) => `<td>${renderInline(c)}</td>`).join("")}</tr>`)
        .join("")}</tbody>`;
      html.push(`<table>${thead}${tbody}</table>`);
      continue;
    }

    // 목록
    const ulItem = line.match(/^\s*[-*+]\s+(.*)$/);
    const olItem = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (ulItem || olItem) {
      flushPara();
      const want: "ul" | "ol" = ulItem ? "ul" : "ol";
      if (listType && listType !== want) flushList();
      if (!listType) {
        html.push(`<${want}>`);
        listType = want;
      }
      const text = ulItem ? ulItem[1] : (olItem as RegExpMatchArray)[1];
      html.push(`<li>${renderInline(text)}</li>`);
      continue;
    }

    // 그 외 — 문단 누적
    flushList();
    paraBuf.push(line.trim());
  }

  if (inCode) html.push(`<pre><code>${escapeHtml(codeBuf.join("\n"))}</code></pre>`);
  flushBlocks();

  return html.join("\n");
}

/* -------------------------------------------------------------------------- */

function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number): (...args: A) => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (...args: A) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

function selectedValues(el: HTMLSelectElement): string[] {
  return Array.from(el.selectedOptions).map((o) => o.value).filter((v) => v !== "");
}

function renderChips(values: string[]): string {
  if (!values.length) return '<span class="admin-project-docs__chip admin-project-docs__chip--empty">—</span>';
  return values
    .map((v) => `<span class="admin-project-docs__chip">${escapeHtml(v)}</span>`)
    .join("");
}

function renderMetaBox(fields: {
  title: string;
  desc: string;
  categories: string[];
  related: string[];
  updatedAt: string;
}): string {
  const row = (k: string, v: string): string =>
    `<div class="admin-project-docs__meta-row"><dt>${escapeHtml(k)}</dt><dd>${v}</dd></div>`;
  return [
    row("title", escapeHtml(fields.title) || '<span class="admin-project-docs__chip--empty">—</span>'),
    row("desc", escapeHtml(fields.desc) || '<span class="admin-project-docs__chip--empty">—</span>'),
    row("categories", renderChips(fields.categories)),
    row("related", renderChips(fields.related)),
    row("updatedAt", escapeHtml(fields.updatedAt) || '<span class="admin-project-docs__chip--empty">—</span>'),
  ].join("");
}

export const AdminProjectDocController = {
  index(): void {
    Logger.debug(`${CTRL_TAG}.index (no-op)`);
  },

  update(): void {
    Logger.debug(`${CTRL_TAG}.update (no-op)`);
  },

  edit(): void {
    const form = document.querySelector<HTMLFormElement>(".admin-project-docs__form");
    const preview = document.querySelector<HTMLElement>("[data-project-doc-preview]");
    const metaBox = document.querySelector<HTMLElement>("[data-project-doc-meta]");
    if (!form || !preview || !metaBox) {
      Logger.debug(`${CTRL_TAG}.edit: form/preview/meta not found — nothing to do`);
      return;
    }

    const titleEl     = form.querySelector<HTMLInputElement>('[data-project-doc-field="title"]');
    const descEl      = form.querySelector<HTMLInputElement>('[data-project-doc-field="desc"]');
    const categoriesEl = form.querySelector<HTMLSelectElement>('[data-project-doc-field="categories"]');
    const relatedEl   = form.querySelector<HTMLSelectElement>('[data-project-doc-field="related"]');
    const updatedAtEl = form.querySelector<HTMLInputElement>('[data-project-doc-field="updatedAt"]');
    const bodyEl      = form.querySelector<HTMLTextAreaElement>('[data-project-doc-field="body"]');

    const sync = (): void => {
      const fields = {
        title: titleEl?.value.trim() ?? "",
        desc: descEl?.value.trim() ?? "",
        categories: categoriesEl ? selectedValues(categoriesEl) : [],
        related: relatedEl ? selectedValues(relatedEl) : [],
        updatedAt: updatedAtEl?.value.trim() ?? "",
      };
      metaBox.innerHTML = renderMetaBox(fields);
      preview.innerHTML = renderMarkdown(bodyEl?.value ?? "");
    };
    const syncDebounced = debounce(sync, 120);

    [titleEl, descEl, updatedAtEl].forEach((el) => {
      el?.addEventListener("input", syncDebounced);
    });
    // <select multiple> 은 SlimSelect 가 변경 시 change 이벤트 발생
    [categoriesEl, relatedEl].forEach((el) => {
      el?.addEventListener("change", syncDebounced);
    });
    if (bodyEl) {
      bodyEl.addEventListener("input", syncDebounced);

      // Tab 키: 포커스 이동 대신 들여쓰기(스페이스 2칸) 삽입
      bodyEl.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.key !== "Tab" || e.ctrlKey || e.metaKey || e.altKey) return;
        e.preventDefault();
        const { selectionStart: start, selectionEnd: end, value } = bodyEl;
        const insert = "  ";
        bodyEl.value = value.slice(0, start) + insert + value.slice(end);
        bodyEl.selectionStart = bodyEl.selectionEnd = start + insert.length;
        syncDebounced();
      });
    }

    sync();
    Logger.debug(`${CTRL_TAG}.edit: live preview ready`);
  },
};
